/** 日期分隔符號 */
type DateSeparator = '/' | '-' | '';
/** 日期與時間之間的分隔符號 */
type DateTimeSeparator = ' ' | '';
/** 時間的分隔符號 */
type TimeSeparator = ':' | '';

export class CustomDate extends Date {
    /**
     * 建構函數，初始化 CustomDate 物件。
     * @param dateInput 可選的日期輸入，可以是 Date 物件或字串。若未提供，則使用當前時間。
     * @param isUTC 是否使用 UTC 時間，預設為 false。
     */
    constructor(dateInput?: Date | string, isUTC: boolean = false) {

        let dateValue: Date;
        if (dateInput === undefined) {
            dateValue = new Date(); // 無輸入時，使用當前時間
        } else if (typeof dateInput === 'string') {
            dateValue = CustomDate.parseDateString(dateInput, isUTC); // 解析字串輸入
        } else {
            dateValue = dateInput; // 使用提供的 Date 物件
        }

        super(dateValue)
    }

    /**
     * 解析日期字串，支持 ISO 8601 和 numeric 格式。
     * @param dateStr 輸入的日期字串。
     * @param isUTC 是否以 UTC 模式解析。
     * @returns 解析後的 Date 物件。
     */
    private static parseDateString(dateStr: string, isUTC: boolean): Date {
        const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z)?$/;
        if (isoRegex.test(dateStr)) {
            // 字串本身若帶 Z 代表後端已明確標示為 UTC，不論呼叫端有沒有指定 isUTC 都必須做時區換算，
            // 否則會把 UTC 的時分秒誤當成本地時間解析，造成日期(尤其是午夜時刻)整整差一天
            if (isUTC || dateStr.endsWith('Z')) {
                const date = new Date(dateStr);
                if (!isNaN(date.getTime())) return date;
            } else {
                // 將 ISO 字串拆解並作為本地時間處理
                const [datePart, timePart] = dateStr.split('T');
                const [year, month, day] = datePart.split('-').map(Number);
                const [time, msPart] = timePart.split('.');
                const [hours, minutes, seconds] = time.split(':').map(Number);
                const milliseconds = msPart ? parseInt(msPart, 10) : 0;
                const date = new Date(year, month - 1, day, hours, minutes, seconds, milliseconds);
                if (!isNaN(date.getTime())) return date;
            }
        }
        return CustomDate.parseNumericDateString(dateStr, isUTC);
    }

    /**
     * 解析 numeric 格式的日期字串（如 YYYYMMDDHHMMSS）。
     * @param dateStr 輸入的日期字串。
     * @param isUTC 是否以 UTC 模式創建日期。
     * @returns 解析後的 Date 物件。
     * @throws 若格式無效或日期創建失敗，拋出錯誤。
     */
    private static parseNumericDateString(dateStr: string, isUTC: boolean): Date {
        const numericRegex = /^\d+$/;
        const allowedLengths = new Set([6, 8, 10, 12, 14, 15, 16, 17]);

        // ✅ 如果是純數字格式
        if (numericRegex.test(dateStr) && allowedLengths.has(dateStr.length)) {
            const parts = CustomDate.extractDateParts(dateStr);

            const date = isUTC
                ? new Date(Date.UTC(parts.year, parts.month, parts.day, parts.hours, parts.minutes, parts.seconds, parts.milliseconds))
                : new Date(parts.year, parts.month, parts.day, parts.hours, parts.minutes, parts.seconds, parts.milliseconds);

            if (isNaN(date.getTime())) {
                throw new Error(`日期創建失敗...`);
            }
            return date;
        }

        // ✅ 嘗試解析 ISO 8601 格式
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) {
            return isoDate;
        }

        throw new Error('無效的日期格式，應為 YYYYMM、YYYYMMDD、YYYYMMDDHHMM、YYYYMMDDHHMMSS、YYYYMMDDHHMMSSmmm 或 ISO 8601');
    }

    /**
     * 從 numeric 日期字串中提取年、月、日等組成部分。
     * @param dateStr 輸入的日期字串。
     * @returns 包含各時間部分的物件。
     */
    private static extractDateParts(dateStr: string): {
        year: number;
        month: number;
        day: number;
        hours: number;
        minutes: number;
        seconds: number;
        milliseconds: number;
    } {
        const year = parseInt(dateStr.substring(0, 4), 10); // 提取年份
        const month = parseInt(dateStr.substring(4, 6), 10) - 1; // 提取月份並減 1（Date 物件使用 0-11）
        let day = 1, hours = 0, minutes = 0, seconds = 0, milliseconds = 0; // 預設值

        if (dateStr.length >= 8) day = parseInt(dateStr.substring(6, 8), 10); // 提取日期
        if (dateStr.length >= 10) hours = parseInt(dateStr.substring(8, 10), 10); // 提取小時
        if (dateStr.length >= 12) minutes = parseInt(dateStr.substring(10, 12), 10); // 提取分鐘
        if (dateStr.length >= 14) seconds = parseInt(dateStr.substring(12, 14), 10); // 提取秒數
        if (dateStr.length >= 15) {
            const msLength = dateStr.length - 14; // 毫秒長度
            milliseconds = parseInt(dateStr.substring(14, 14 + msLength), 10) * (msLength === 1 ? 100 : msLength === 2 ? 10 : 1); // 計算毫秒
        }

        return { year, month, day, hours, minutes, seconds, milliseconds };
    }

    /**
     * 獲取格式化的日期組成部分。
     * @returns 包含年、月、日等的格式化字串物件。
     */
    private getFormattedParts() {
        return {
            year: this.getFullYear().toString(),
            month: this.formatNumber(this.getMonth() + 1), // 月份加 1（Date 物件使用 0-11）
            date: this.formatNumber(this.getDate()),
            hours: this.formatNumber(this.getHours()),
            minutes: this.formatNumber(this.getMinutes()),
            seconds: this.formatNumber(this.getSeconds()),
            milliseconds: this.getMilliseconds().toString().padStart(3, '0'), // 毫秒補齊 3 位
        };
    }

    /**
     * 獲取當月的第一天的日期。
     * @returns 當月第一天的日期。
     */
    getFirstDayDateOfMonth(): string {
        return this.toYYYYMMDD().substring(0, 6) + "01";
    }

    /**
     * 獲取當月的最後一天的日期。
     * @returns 當月最後一天的日期。
     */
    getLastDayDateOfMonth(): string {
        const year = this.getFullYear();
        const month = this.getMonth();
        const lastDay = new Date(year, month + 1, 0);
        return new CustomDate(lastDay).toYYYYMMDD();
    }

    /**
     * 獲取日期是星期幾。
     * @param num 要格式化的數字。
     * @returns 星期字串。
     */
    getDayName = (): string => {
        let d = this.getDay().toString();
        let week: { [key: string]: string } = { "0": "日", "1": "一", "2": "二", "3": "三", "4": "四", "5": "五", "6": "六" };
        return week[d];
    }

    /**
     * 將數字格式化為兩位數字串。
     * @param num 要格式化的數字。
     * @returns 補零後的兩位數字串。
     */
    private formatNumber(num: number): string {
        return num.toString().padStart(2, '0');
    }

    /**
    * 格式化日期為 hhmm 格式。
    * @param timeSeparator 小時分鐘之間的分隔符，預設為空。
    * @returns 格式化後的字串。
    */
    toHHmm(timeSeparator: TimeSeparator = ''): string {
        const { hours, minutes } = this.getFormattedParts();
        return `${hours}${timeSeparator}${minutes}`;
    }

    /**
    * 格式化日期為 HHmmSS 格式。
    * @param timeSeparator 時間部分之間的分隔符，預設為空。
    * @returns 格式化後的字串。
    */
    toHHmmSS(timeSeparator: TimeSeparator = ''): string {
        const { hours, minutes, seconds } = this.getFormattedParts();
        return `${hours}${timeSeparator}${minutes}${timeSeparator}${seconds}`;
    }

    /**
    * 格式化日期為 MMdd 格式。
    * @param dateSeparator 月日之間的分隔符，預設為空。
    * @returns 格式化後的字串。
    */
    toMMdd(dateSeparator: DateSeparator = ''): string {
        const { month, date } = this.getFormattedParts();
        return `${month}${dateSeparator}${date}`;
    }

    /**
     * 格式化日期為 YYYYMM 格式。
     * @param dateSeparator 年月之間的分隔符，預設為空。
     * @returns 格式化後的字串。
     */
    toYYYYMM(dateSeparator: DateSeparator = ''): string {
        const { year, month } = this.getFormattedParts();
        return `${year}${dateSeparator}${month}`;
    }

    /**
     * 格式化日期為 YYYYMMDD 格式。
     * @param dateSeparator 年月日之間的分隔符，預設為空。
     * @returns 格式化後的字串。
     */
    toYYYYMMDD(dateSeparator: DateSeparator = ''): string {
        const { year, month, date } = this.getFormattedParts();
        return `${year}${dateSeparator}${month}${dateSeparator}${date}`;
    }

    /**
     * 格式化日期為 YYYYMMDDHHMM 格式。
     * @param dateSeparator  年月日之間的分隔符，預設為空。
     * @param dateTimeSeparator  日期與時間之間的分隔符，預設為空。
     * @param timeSeparator 時間部分之間的分隔符，預設為空。
     * @returns 格式化後的字串。
     */
    toYYYYMMDDHHMM(dateSeparator: DateSeparator = '', dateTimeSeparator: DateTimeSeparator = '', timeSeparator: TimeSeparator = ''): string {
        const { year, month, date, hours, minutes } = this.getFormattedParts();
        return `${year}${dateSeparator}${month}${dateSeparator}${date}${dateTimeSeparator}${hours}${timeSeparator}${minutes}`;
    }

    /**
     * 格式化日期為 YYYYMMDDHHMMSS 格式，可選包含毫秒。
     * @param dateSeparator 年月日之間的分隔符，預設為空。
     * @param dateTimeSeparator 日期與時間之間的分隔符，預設為空。
     * @param timeSeparator 時間部分之間的分隔符，預設為空。
     * @param includeMilliseconds 是否包含毫秒，預設為 false。
     * @returns 格式化後的字串。
     */
    toYYYYMMDDHHMMSS(
        dateSeparator: DateSeparator = '',
        dateTimeSeparator: DateTimeSeparator = '',
        timeSeparator: TimeSeparator = '',
        includeMilliseconds: boolean = false
    ): string {
        const { year, month, date, hours, minutes, seconds, milliseconds } = this.getFormattedParts();
        let formattedDate = `${year}${dateSeparator}${month}${dateSeparator}${date}${dateTimeSeparator}${hours}${timeSeparator}${minutes}${timeSeparator}${seconds}`;

        if (includeMilliseconds) {
            formattedDate += `.${milliseconds}`; // 添加毫秒
        }
        return formattedDate;
    }

    /**
     * 自定義格式化日期格式。
     * @param format 自定義的格式, 分割綫可直接寫在裏面
     * @returns 格式化後的字串。
     * @example .toCustomFormat("MMdd"), output: "0327"
     * @example .toCustomFormat("yyyy/MM/dd HH:mmm:ss"), output "2025/03/27 11:56:23"
     */
    toCustomFormat(format: string): string {
        const { year, month, date, hours, minutes, seconds, milliseconds } = this.getFormattedParts();

        // 定義格式對應的值
        const formatMap: Record<string, string> = {
            'yyyy': year.toString(),//完整年份
            'yy': year.toString().slice(-2),//年份后兩位
            'MM': month.toString(),//月份
            'dd': date.toString(),//日
            'HH': hours.toString(),//小時
            'mm': minutes.toString(),//分鐘
            'ss': seconds.toString(),//秒
            'SSS': milliseconds.toString(),//毫秒
        };

        // 使用正則替換格式標記
        return format.replace(/yyyy|yy|MM|dd|HH|mm|ss|SSS/g, match => formatMap[match] || match);
    }

    /**
     * 取得哪個日期開始不能操作
     * @param {number} year 年
     * @param {number} month 月
     * @param {number} day 日
     * @returns 
     */
    static getDisableDate(year: number = 0, month: number = 0, day: number = 0): string {
        const currentDate = new Date();

        // 計算完整的年份和月份
        const totalMonths = month;
        const years = Math.floor(Math.abs(totalMonths) / 12);
        const months = Math.abs(totalMonths) % 12;

        // 設置年份
        currentDate.setFullYear(
            totalMonths < 0
                ? currentDate.getFullYear() + year + years
                : currentDate.getFullYear() - year - years
        );

        // 設置月份，考慮天數的變化
        let targetMonth =
            totalMonths < 0
                ? currentDate.getMonth() + months
                : currentDate.getMonth() - months;
        let targetYear = currentDate.getFullYear();

        if (targetMonth < 0) {
            targetMonth += 12;
            targetYear -= 1;
        }
        if (targetMonth > 11) {
            targetMonth -= 12;
            targetYear += 1;
        }

        const targetDate = new Date(targetYear, targetMonth + 1, 0); // 將日期設置為目標月份的最後一天

        if (currentDate.getDate() > targetDate.getDate()) {
            currentDate.setDate(targetDate.getDate());
        }

        // 設置月份和日期
        currentDate.setFullYear(targetDate.getFullYear());
        currentDate.setMonth(targetMonth);
        currentDate.setDate(currentDate.getDate() - day);
        return new CustomDate(currentDate).toYYYYMMDD();
    };

    /**
     * @description 獲取一個日期的最後一秒，常用於查詢條件的“訖日期”
     * @description 輸入什麼格式，回傳同格式的資料：Date → Date、string → YYYYMMDDHHMMSS格式 string、null/undefined → null
     */
    static getLastSecondOfTheDay(dateInput: Date | undefined | null): Date | null;
    static getLastSecondOfTheDay(dateInput: string | undefined | null): string | null;
    static getLastSecondOfTheDay(dateInput: Date | string | undefined | null): Date | string | null {
        if (!dateInput) return null;

        const time = new CustomDate(dateInput as Date | string);
        time.setHours(23, 59, 59, 0);

        if (typeof dateInput === 'string') {
            return time.toYYYYMMDDHHMMSS();
        }

        return new Date(time.getTime());
    }
}
